import React, { useCallback, useMemo, useState } from "react";
import Blockies from "react-blockies";
import {
  Avatar,
  CircularProgress,
  useTheme,
} from "@mui/material";
import punycode from "punycode";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import { BasicProfile } from "protico-sdk";
import { useAppSelector } from "../../../hooks";
import { useDispatch } from "react-redux";
import { TBToCMessage } from "../../../types/types";
import { extractDomain, isAssetUrl } from "../../../utils/format.utils";
import { useBlockList } from "../../../hooks/useBlockList";
import {
  setNext,
  setPrevious,
  initRoomInfomation,
} from "../../../store/reducers/bToCState";
import {
  MessageOptionButton,
  MessageOptionButtonGroup,
  MessageOptionContainer,
  MessageOptionWrapper,
  RepliedMessageContainer,
  RepliedMessageContainerReverse,
  MessageHeader,
} from "../../WhiteBoard/WhiteBoardMessage/WhiteBoardMessage.styles";
import ReferenceActionIcon from "@mui/icons-material/Reply";
import { useBToCWebsocket } from "../../../hooks/useBtoCWebsocket";
import { getProticoClient } from "../../../utils/api";
import useBasicProfile from "../../../hooks/useBasicProfile";

const componentDecorator = (href: any, text: any, key: any) => (
  <a
    href={href}
    key={key}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: "inherit" }}
  >
    {punycode.toASCII(text)}
  </a>
);
type Prop = {
  profileData: BasicProfile | undefined;
  message: TBToCMessage;
  toDomain: string;
  scrollToBottom: () => void;
  setbuyModalstatus: any;
  roomLogo: string;
};
const ChatItem: React.FC<Prop> = ({
  profileData,
  message,
  toDomain,
  roomLogo,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { isUserProfileFetching } = useAppSelector((state) => state.profile);
  const { basicProfile: userProfile } = useBasicProfile(currentAddress);
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslation();
  const { secondFontColor, fontColor } = useAppSelector(
    (state) => state.layout
  );
  const { ifHyperlink } = useAppSelector((state) => state.settings);
  const { sended, createdAt, fromAddress, messageText, id } = message;
  const { domainAllowList } = useBlockList();
  const { handleSetNextReference } = useBToCWebsocket();
  const AvatarEl = useMemo(() => {
    if (isUserProfileFetching)
      return <CircularProgress style={{ color: theme.brandColor }} size={20} />;
    return userProfile.avatar_url ? (
      <Avatar
        src={userProfile.avatar_url}
        style={{
          width: "27px",
          height: "27px",
          cursor: "pointer",
        }}
      />
    ) : (
      <Blockies
        seed={currentAddress}
        size={10}
        scale={2.7}
        color="#ddd"
        className="identicon"
      />
    );
  }, [userProfile?.avatar_url, isUserProfileFetching]);
  const referenceMessageId = message.message_reference?.message_id;
  const referenced_message = message.referenced_message;
  const msgTime = () => {
    const today = new Date();
    const messageTime = new Date(createdAt);
    if (
      today.toDateString().slice(-4) === messageTime.toDateString().slice(-4)
    ) {
      if (today.toDateString() === messageTime.toDateString()) {
        // For the today messages, we only display the time
        return messageTime.toTimeString().slice(0, 5);
      }
      // For the messages sent in the current year, we only display the date without year info
      return (
        messageTime.toLocaleString("en-GB").slice(12, 17) +
        ", " +
        messageTime.toLocaleString("en-GB").slice(0, 5)
      );
    }
    return messageTime.toLocaleString("en-GB").slice(0, 17);
  };

  const messageFromMe = message.messageType == "c_to_b";

  const isDomainInList = useCallback(
    (msg: string) => {
      const domain = extractDomain(msg)?.toLowerCase();
      if (!domain) return false;
      return domainAllowList.includes(domain);
    },
    [JSON.stringify(domainAllowList)]
  );

  const onReferenceActionClick = () => {
    handleSetNextReference({
      message_id: message.id,
      sender_address:
        message.messageType == "c_to_b"
          ? message.fromAddress
          : message.toDomain,
      messageText: isAssetUrl(messageText)
        ? t("Message_Item_Refer_Image")
        : messageText,
    });
  };

  const onReferenceClick = async () => {
    if (!referenceMessageId) return;

    const res = await getProticoClient().bToCMessages.getHistory(
      fromAddress,
      toDomain,
      referenceMessageId
    );
    const resMessages = res.results;
    if (resMessages.length !== 0) {
      dispatch(
        setNext({
          id: resMessages[0].id,
          createdAt: resMessages[0].createdAt,
          url: res.next,
        })
      );
      dispatch(
        setPrevious({
          id: resMessages[resMessages.length - 1].id,
          createdAt: resMessages[resMessages.length - 1].createdAt,
          url: res.previous,
        })
      );

      const processedMessages = resMessages.map((historyMessage) => {
        try {
          return {
            ...historyMessage,
            sended: true,
          } as unknown as TBToCMessage;
        } catch (_) {
          return undefined;
        }
      });
      dispatch(
        initRoomInfomation({
          toDomain,
          historyMessageList: processedMessages,
        })
      );
    } else {
      dispatch(setNext(null));
      dispatch(setPrevious(null));
    }
    const maxAttempts = 20;
    let attempts = 0;
    // Define a function to check for the element
    const checkForElement = () => {
      attempts += 1;
      // Find the element by its unique id
      const element = document.getElementById(
        `chat-message-content-${referenceMessageId}`
      );

      // If the element exists, scroll to it
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
          // Add the 'msg-item-shining-effect' class
          element.classList.add("msg-item-shining-effect");
        }, 100);

        // Remove the 'msg-item-shining-effect' class after 1 second
        setTimeout(() => {
          element.classList.remove("msg-item-shining-effect");
        }, 2000);
      } else if (attempts < maxAttempts) {
        // If the element does not exist, check again after a short delay
        setTimeout(checkForElement, 100);
      }
    };

    // Start checking for the element
    checkForElement();
  };
  const parse_url_first_letter = (url: string) => {
    if (url.includes("https://")) {
      return url.split("https://")[1][0].toUpperCase();
    } else if (url.includes("http://")) {
      return url.split("http://")[1][0].toUpperCase();
    } else {
      return url[0].toUpperCase();
    }
  };

  // TODO: use message.sended here
  return (
    <>
      <div
        id={`chat-message-content-${id}`}
        className={`chat-item-message-container ${
          messageFromMe ? "currentUser" : ""
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <li
          style={{ flexDirection: "column" }}
          className={`chat-item-messages ${messageFromMe ? "currentUser" : ""}`}
        >
          {referenceMessageId &&
            !!referenced_message &&
            !referenced_message.hidden &&
            (messageFromMe ? (
              <MessageHeader style={{ width: "-webkit-fill-available" }}>
                <RepliedMessageContainerReverse>
                  <p 
                    style={{
                      border: "1px solid #666",
                      padding: "5px 7px",
                      borderRadius: "10px",
                    }}
                    onClick={onReferenceClick}
                  >
                    {isAssetUrl(referenced_message.messageText)
                      ? t("Message_Item_Refer_Image")
                      : referenced_message.messageText}
                  </p>
                </RepliedMessageContainerReverse>
              </MessageHeader>
            ) : (
              <MessageHeader>
                <RepliedMessageContainer>
                  <p
                    style={{
                      border: "1px solid #666",
                      padding: "5px 7px",
                      borderRadius: "10px",
                    }}
                    onClick={onReferenceClick}
                  >
                    {isAssetUrl(referenced_message.messageText)
                      ? t("Message_Item_Refer_Image")
                      : referenced_message.messageText}
                  </p>
                </RepliedMessageContainer>
              </MessageHeader>
            ))}
          <div
            style={{ display: "flex" }}
            className={`${messageFromMe ? "row-reverse" : ""}`}
          >
            <div
              className={`d2c-item-content-container ${
                messageFromMe ? "currentUser" : ""
              }`}
            >
              <div
                className={`d2c-item-content ${
                  messageFromMe ? "currentUser" : ""
                }`}
              >
                <div
                  className="d2c-item-content-text"
                  style={{ color: sended ? fontColor : "#808080" }}
                >
                  {ifHyperlink || isDomainInList(messageText) ? (
                    <Linkify componentDecorator={componentDecorator}>
                      {messageText}
                    </Linkify>
                  ) : (
                    messageText
                  )}
                </div>
              </div>
              
              <div className="d2c-item-message-time-container">
              {isHovered &&
                <div
                  className="d2c-item-time"
                  style={{ color: secondFontColor }}
                >
                  {!!createdAt && msgTime()}
                </div>
              }
              </div>
            </div>
            <MessageOptionContainer style={{ position: "relative", margin: "auto 0px 3px 10px" }}>
              <MessageOptionWrapper
                active={isHovered}
                style={{ position: "relative", top: 0, padding: 0 }}
              >
                <MessageOptionButtonGroup>
                  <MessageOptionButton onClick={onReferenceActionClick} style={{ margin: 0 }}>
                    <ReferenceActionIcon className="d2c-message-option-icon" />
                  </MessageOptionButton>
                </MessageOptionButtonGroup>
              </MessageOptionWrapper>
            </MessageOptionContainer>
          </div>
        </li>
      </div>
    </>
  );
};

export default ChatItem;
